import { useEffect, useMemo, useRef, useState } from 'react';
import type { Artifact, Member } from '../api/client';
import ArtifactRow from './ArtifactRow';

export interface ArtifactListFilters {
  name: string;
  ownerId: string;
  type: string;
  createdFrom: string;
  createdTo: string;
  updatedFrom: string;
  updatedTo: string;
}

interface ArtifactListProps {
  artifacts: Artifact[];
  total: number;
  members: Member[];
  view: 'active' | 'archived';
  onViewChange: (view: 'active' | 'archived') => void;
  onSortChange: (sort: 'recent' | 'alphabetical' | 'starred' | 'starred_only') => void;
  sort: 'recent' | 'alphabetical' | 'starred' | 'starred_only';
  onArtifactsChanged: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filters: ArtifactListFilters;
  onFiltersChange: (filters: ArtifactListFilters) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

const FILE_TYPE_OPTIONS = [
  { value: '', label: 'Any type' },
  { value: 'text/html', label: 'HTML' },
  { value: 'application/pdf', label: 'PDF' },
  { value: 'text/markdown', label: 'Markdown' },
];

function getFilterCount(filters: ArtifactListFilters): number {
  return [
    filters.ownerId,
    filters.type,
    filters.createdFrom,
    filters.createdTo,
    filters.updatedFrom,
    filters.updatedTo,
  ].filter(Boolean).length;
}

function formatTypeLabel(type: string): string {
  return FILE_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type;
}

export default function ArtifactList({
  artifacts,
  total,
  members,
  view,
  onViewChange,
  onSortChange,
  sort,
  onArtifactsChanged,
  searchValue,
  onSearchChange,
  filters,
  onFiltersChange,
  hasMore,
  loadingMore,
  onLoadMore,
}: ArtifactListProps) {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const activeFilterCount = getFilterCount(filters);
  const starredCount = artifacts.filter((artifact) => artifact.starred).length;

  const ownerOptions = useMemo(
    () =>
      members
        .slice()
        .sort((left, right) => left.user.name.localeCompare(right.user.name)),
    [members],
  );

  useEffect(() => {
    if (!hasMore || loadingMore) return;
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: '240px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  const sortLabel =
    view === 'archived'
      ? 'Archived'
      : sort === 'alphabetical'
      ? 'Alphabetical'
      : sort === 'starred'
        ? 'Starred first'
        : sort === 'starred_only'
          ? 'Starred only'
          : 'Recent';

  const ownerLabel = ownerOptions.find((member) => member.id === filters.ownerId)?.user.name ?? '';
  const hasSearch = searchValue.trim().length > 0;
  const hasAnyQuery = hasSearch || activeFilterCount > 0 || sort !== 'recent' || view === 'archived';

  const filterChips = [
    ownerLabel ? `Owner: ${ownerLabel}` : null,
    filters.type ? `Type: ${formatTypeLabel(filters.type)}` : null,
    filters.createdFrom || filters.createdTo
      ? `Created: ${filters.createdFrom || 'Any'} to ${filters.createdTo || 'Any'}`
      : null,
    filters.updatedFrom || filters.updatedTo
      ? `Modified: ${filters.updatedFrom || 'Any'} to ${filters.updatedTo || 'Any'}`
      : null,
    hasSearch ? `Name: ${searchValue.trim()}` : null,
    view === 'archived'
      ? 'View: Archived'
      : sort !== 'recent'
        ? `Sort: ${sortLabel}`
        : null,
  ].filter(Boolean) as string[];

  const clearFilters = () => {
    onSortChange('recent');
    onViewChange('active');
    onFiltersChange({
      name: '',
      ownerId: '',
      type: '',
      createdFrom: '',
      createdTo: '',
      updatedFrom: '',
      updatedTo: '',
    });
    onSearchChange('');
    setIsFilterOpen(false);
  };

  return (
    <section>
      <div
        className="px-[var(--sp-6)] pt-[16px] pb-[var(--sp-2)]"
      >
        <div className="flex items-center gap-[var(--sp-3)]">
          {/* View type dropdown (replaces "Artifacts" heading) */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => {
                setIsSortOpen((current) => !current);
                setIsFilterOpen(false);
              }}
              className="inline-flex items-center gap-[6px] h-[36px] border-0 bg-transparent cursor-pointer"
              style={{ padding: 0 }}
            >
              <span className="text-[18px] font-bold" style={{ color: 'var(--rd-text-strong)' }}>
                {sortLabel}
              </span>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--rd-text-meta)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6l4 4 4-4" /></svg>
            </button>
            {isSortOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsSortOpen(false)} />
                <div
                  className="absolute left-0 top-full mt-[var(--sp-1)] z-20 min-w-[180px] rounded-[10px] py-[var(--sp-1)]"
                  style={{
                    background: 'var(--rd-surface-panel)',
                    border: '1px solid var(--rd-border)',
                    boxShadow: 'var(--rd-dropdown-shadow)',
                  }}
                >
                  {[
                    { value: 'recent', label: 'Recent' },
                    { value: 'alphabetical', label: 'Alphabetical' },
                    { value: 'starred', label: 'Starred first' },
                    { value: 'starred_only', label: `Starred only (${starredCount})` },
                    { value: 'archived', label: 'Archived' },
                  ].map((option) => {
                    const isSelected = option.value === 'archived' ? view === 'archived' : view === 'active' && sort === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          if (option.value === 'archived') {
                            onViewChange('archived');
                            onSortChange('recent');
                          } else {
                            onViewChange('active');
                            onSortChange(option.value as ArtifactListProps['sort']);
                          }
                          setIsSortOpen(false);
                        }}
                        className="w-full text-left px-[var(--sp-4)] py-[var(--sp-2)] text-[13px] font-medium cursor-pointer transition-colors duration-[120ms] border-0"
                        style={{
                          color: isSelected ? 'var(--rd-text-strong)' : 'var(--rd-text-medium)',
                          background: isSelected ? 'var(--rd-surface-hover)' : 'transparent',
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--rd-surface-hover)'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {hasAnyQuery && (
            <span className="text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
              {artifacts.length} of {total}
            </span>
          )}

          <div className="flex-1" />

          <div className="flex items-center gap-[var(--sp-2)]">
            <div className="relative">
              <label
                className="flex items-center gap-[var(--sp-2)] h-[36px] min-w-[320px] rounded-[8px] border px-[var(--sp-3)]"
                style={{
                  background: 'var(--rd-surface-input)',
                  borderColor: isFilterOpen ? 'var(--rd-accent)' : 'var(--rd-border)',
                }}
              >
                <i
                  className="icon icon-app-search"
                  style={{ color: 'var(--rd-text-placeholder)', fontSize: 14, flexShrink: 0 }}
                />
                <input
                  value={searchValue}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder="Search artifact names"
                  className="w-full h-full border-0 bg-transparent text-[13px] outline-none"
                  style={{ color: 'var(--rd-text-strong)' }}
                />
                {activeFilterCount > 0 && (
                  <span
                    className="inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full px-[4px] text-[13px] font-semibold flex-shrink-0"
                    style={{
                      background: 'var(--rd-accent)',
                      color: '#fff',
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsFilterOpen((current) => !current);
                    setIsSortOpen(false);
                  }}
                  className="flex items-center justify-center w-[24px] h-[24px] rounded-[4px] border-0 bg-transparent cursor-pointer flex-shrink-0 transition-colors duration-[120ms]"
                  style={{
                    color: isFilterOpen ? 'var(--rd-accent)' : 'var(--rd-text-placeholder)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--rd-text-strong)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = isFilterOpen ? 'var(--rd-accent)' : 'var(--rd-text-placeholder)')}
                  title="Filters"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                    <path d="M2 4h12M4 8h8M6 12h4" />
                  </svg>
                </button>
              </label>

              {isFilterOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsFilterOpen(false)} />
                  <div
                    className="absolute right-0 top-full z-20 mt-[var(--sp-1)] w-[360px] max-w-[calc(100vw-32px)] rounded-[10px] border p-[var(--sp-4)]"
                    style={{
                      background: 'var(--rd-surface-panel)',
                      borderColor: 'var(--rd-border)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p
                          className="m-0 text-[13px] font-semibold"
                          style={{ color: 'var(--rd-text-strong)' }}
                        >
                          Filter artifacts
                        </p>
                        <p
                          className="m-0 mt-[4px] text-[13px]"
                          style={{ color: 'var(--rd-text-subtle)' }}
                        >
                          Narrow by owner, file type, name, or dates.
                        </p>
                      </div>
                      {hasAnyQuery && (
                        <button
                          onClick={clearFilters}
                          className="border-0 bg-transparent p-0 text-[13px] font-semibold cursor-pointer"
                          style={{ color: 'var(--rd-text-medium)' }}
                        >
                          Clear all
                        </button>
                      )}
                    </div>

                    <div className="mt-[var(--sp-4)] grid grid-cols-1 gap-[var(--sp-3)]">
                      <label className="flex flex-col gap-[var(--sp-1)]">
                        <span className="text-[13px] font-medium" style={{ color: 'var(--rd-text-subtle)' }}>
                          Name
                        </span>
                        <input
                          value={searchValue}
                          onChange={(event) => onSearchChange(event.target.value)}
                          placeholder="Search by title"
                          className="h-[40px] rounded-[8px] border px-[var(--sp-3)] text-[13px] outline-none"
                          style={{
                            background: 'var(--rd-surface-input)',
                            borderColor: 'var(--rd-border)',
                            color: 'var(--rd-text-strong)',
                          }}
                        />
                      </label>

                      <label className="flex flex-col gap-[var(--sp-1)]">
                        <span className="text-[13px] font-medium" style={{ color: 'var(--rd-text-subtle)' }}>
                          Owner
                        </span>
                        <select
                          value={filters.ownerId}
                          onChange={(event) => onFiltersChange({ ...filters, ownerId: event.target.value })}
                          className="h-[40px] rounded-[8px] border px-[var(--sp-3)] text-[13px]"
                          style={{
                            background: 'var(--rd-surface-input)',
                            borderColor: 'var(--rd-border)',
                            color: 'var(--rd-text-medium)',
                          }}
                        >
                          <option value="">Any owner</option>
                          {ownerOptions.map((member) => (
                            <option key={member.id} value={member.id}>
                              {member.user.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="flex flex-col gap-[var(--sp-1)]">
                        <span className="text-[13px] font-medium" style={{ color: 'var(--rd-text-subtle)' }}>
                          Type
                        </span>
                        <select
                          value={filters.type}
                          onChange={(event) => onFiltersChange({ ...filters, type: event.target.value })}
                          className="h-[40px] rounded-[8px] border px-[var(--sp-3)] text-[13px]"
                          style={{
                            background: 'var(--rd-surface-input)',
                            borderColor: 'var(--rd-border)',
                            color: 'var(--rd-text-medium)',
                          }}
                        >
                          {FILE_TYPE_OPTIONS.map((option) => (
                            <option key={option.label} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <div className="grid grid-cols-2 gap-[var(--sp-2)]">
                        <label className="flex flex-col gap-[var(--sp-1)]">
                          <span className="text-[13px] font-medium" style={{ color: 'var(--rd-text-subtle)' }}>
                            Date created from
                          </span>
                          <input
                            type="date"
                            value={filters.createdFrom}
                            onChange={(event) => onFiltersChange({ ...filters, createdFrom: event.target.value })}
                            className="h-[40px] rounded-[8px] border px-[var(--sp-3)] text-[13px]"
                            style={{
                              background: 'var(--rd-surface-input)',
                              borderColor: 'var(--rd-border)',
                              color: 'var(--rd-text-strong)',
                            }}
                          />
                        </label>
                        <label className="flex flex-col gap-[var(--sp-1)]">
                          <span className="text-[13px] font-medium" style={{ color: 'var(--rd-text-subtle)' }}>
                            Date created to
                          </span>
                          <input
                            type="date"
                            value={filters.createdTo}
                            onChange={(event) => onFiltersChange({ ...filters, createdTo: event.target.value })}
                            className="h-[40px] rounded-[8px] border px-[var(--sp-3)] text-[13px]"
                            style={{
                              background: 'var(--rd-surface-input)',
                              borderColor: 'var(--rd-border)',
                              color: 'var(--rd-text-strong)',
                            }}
                          />
                        </label>
                      </div>

                      <div className="grid grid-cols-2 gap-[var(--sp-2)]">
                        <label className="flex flex-col gap-[var(--sp-1)]">
                          <span className="text-[13px] font-medium" style={{ color: 'var(--rd-text-subtle)' }}>
                            Date modified from
                          </span>
                          <input
                            type="date"
                            value={filters.updatedFrom}
                            onChange={(event) => onFiltersChange({ ...filters, updatedFrom: event.target.value })}
                            className="h-[40px] rounded-[8px] border px-[var(--sp-3)] text-[13px]"
                            style={{
                              background: 'var(--rd-surface-input)',
                              borderColor: 'var(--rd-border)',
                              color: 'var(--rd-text-strong)',
                            }}
                          />
                        </label>
                        <label className="flex flex-col gap-[var(--sp-1)]">
                          <span className="text-[13px] font-medium" style={{ color: 'var(--rd-text-subtle)' }}>
                            Date modified to
                          </span>
                          <input
                            type="date"
                            value={filters.updatedTo}
                            onChange={(event) => onFiltersChange({ ...filters, updatedTo: event.target.value })}
                            className="h-[40px] rounded-[8px] border px-[var(--sp-3)] text-[13px]"
                            style={{
                              background: 'var(--rd-surface-input)',
                              borderColor: 'var(--rd-border)',
                              color: 'var(--rd-text-strong)',
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

          </div>
        </div>

        {filterChips.length > 0 && (
          <div className="mt-[var(--sp-3)] flex flex-wrap gap-[var(--sp-2)]">
            {filterChips.map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center h-[28px] rounded-[100px] px-[var(--sp-3)] text-[13px] font-medium"
                style={{
                  background: 'var(--rd-surface-highlight)',
                  color: 'var(--rd-accent)',
                }}
              >
                {chip}
              </span>
            ))}
            <button
              onClick={clearFilters}
              className="h-[28px] rounded-[100px] border-0 bg-transparent px-[var(--sp-2)] text-[13px] font-semibold cursor-pointer"
              style={{ color: 'var(--rd-text-medium)' }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      <div role="list" aria-label="Artifact list" className="px-[var(--sp-6)] pb-[var(--sp-6)]">
        {artifacts.map((artifact) => (
          <ArtifactRow
            key={artifact.id}
            artifact={artifact}
            onStarChanged={onArtifactsChanged}
          />
        ))}
      </div>

      <div ref={loadMoreRef} />

      {loadingMore && (
        <div className="flex items-center justify-center py-[var(--sp-5)]">
          <div
            className="w-[24px] h-[24px] border-[2px] rounded-full animate-spin"
            style={{
              borderColor: 'var(--rd-border)',
              borderTopColor: 'var(--rd-text-strong)',
            }}
          />
        </div>
      )}
    </section>
  );
}
